import React, { FC, useEffect, useState } from 'react';
import { SurgeFilledIcon } from 'cokitchen-icons';
import { toast } from 'react-toastify';
import { BaseModal, Button, Multiselect, ToastMessage } from '@cokitchen/cokitchen-components';
import { apiService } from 'utils/api';
import { MultiselectItem } from '@cokitchen/cokitchen-components/dist/form/multiselect/types';
import { UserType } from 'models/user.model';
import { IssueType } from 'models/issue.model';
import { ENUM_CS_ROLE } from '@cokitchen/cokitchen-auth';
import { getUserName } from 'utils/functions';

interface EscalateIssueModalProps {
  show: boolean;
  onClose: () => void;
  issue: IssueType;
}

interface EscalateIssueFooterProps {
  loading: boolean;
  disabled: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const EscalateIssueFooter: FC<EscalateIssueFooterProps> = ({
  onCancel,
  onConfirm,
  loading,
  disabled
}) => (
  <div className='grid grid-cols-2 gap-x-4'>
    <Button small color='primary' variant='outline' className='mx-auto' onClick={onCancel}>
      Cancel
    </Button>
    <Button
      small
      color='primary'
      variant='block'
      className='mx-auto'
      loading={loading}
      disabled={loading || disabled}
      onClick={onConfirm}>
      Confirm
    </Button>
  </div>
);

const EscalateIssueModal: FC<EscalateIssueModalProps> = ({ show, onClose, issue }) => {
  const [loading, setLoading] = useState(false);
  const [csLeads, setCsLeads] = useState<MultiselectItem[]>([]);
  const [selectedCsRep, setSelectedCsRep] = useState<string[]>();
  const cancelTrip = async () => {
    const data = {
      issue_id: issue?.id,
      user_id: selectedCsRep?.[0] ?? ''
    };
    try {
      setLoading(true);
      await apiService.post(`cs/issue/escalate`, {
        data,
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      toast(<ToastMessage type='success' message='Order Escalated successfully' />);
      onClose();
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err.response.data.message} />);
    } finally {
      setLoading(false);
    }
  };

  const getCSSuperAdmins = async () => {
    try {
      const res = await apiService.get('cs/staff/all/list', {
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      const csList: UserType[] = res?.data?.data?.filter(
        (item: UserType) => item.role === ENUM_CS_ROLE.CS_SUPER_ADMIN
      );
      const csLeads = csList.map((item: any) => {
        return {
          text: getUserName(item),
          value: item.id
        };
      });
      setCsLeads(csLeads ?? []);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (show) getCSSuperAdmins();
  }, [show]);

  return (
    <BaseModal
      isOpen={show}
      title='Escalate Issue'
      description={`Escalate Issue #${issue?.order?.order_code?.toUpperCase()} to a lead`}
      titleIcon={<SurgeFilledIcon />}
      titleIconColor='gray'
      onRequestClose={onClose}
      footer={
        <EscalateIssueFooter
          disabled={selectedCsRep?.length !== 1}
          loading={loading}
          onCancel={onClose}
          onConfirm={cancelTrip}
        />
      }>
      <div>
        <label className='text-sm font-medium'>Select a lead</label>
        <Multiselect
          onChange={setSelectedCsRep}
          options={csLeads}
          values={selectedCsRep ?? []}
          placeholder='Select A Lead'
          isSingle
        />
      </div>
    </BaseModal>
  );
};

export default EscalateIssueModal;
